/**
 * 演画设计器 Zustand Store — 组合 document / history / selection / viewport / tools slices
 */
import { create } from 'zustand';
import { enablePatches } from 'immer';
import type { MutableRefObject } from 'react';
import { createHistoryEngine, createHistoryActions } from '@/components/canvas/document/history/createHistoryEngine';
import { createDocumentSlice } from './slices/document.slice';
import { createViewportSlice } from './slices/viewport.slice';
import { createSelectionSlice } from './slices/selection.slice';
import { createToolsSlice } from './slices/tools.slice';
import { createBlocksSlice } from './slices/blocks.slice';
import { createLoadStateAction } from './slices/loadState';
import { DEFAULT_WORKSPACE_PAGE_LAYOUT_MODE } from '@/components/canvas/workspace/model/workspacePageLayoutMode';
import type { FullStore } from './types';

export type {
  CanvasState,
  CanvasActions,
  CanvasStore,
  CanvasContextValue,
  FullStore,
  HistorySnapshot,
} from './types';

enablePatches();

export const useCanvasStore = create<FullStore>()((set, get) => {
  /** 当前激活页 `data-page` DOM（与 `activePageRef` 同步，供 dnd-kit / 插入元素等） */
  const activePageDomRef: MutableRefObject<HTMLDivElement | null> = { current: null };
  /** Workspace stageHost，供面板拖图等世界坐标换算 */
  const stageHostDomRef: MutableRefObject<HTMLDivElement | null> = { current: null };

  const { mutate, mutateSilent } = createHistoryEngine({ set, get });

  const nextZ = () => {
    const s = get();
    const page = s.pages.find((cv) => cv.id === s.currentPageId);
    if (page) return page.elements.length;
    return s.workspaceElements.length;
  };

  const deps = { set, get, mutate, mutateSilent, nextZ };

  return {
    pages: [],
    currentPageId: '',
    workspaceElements: [],
    zoom: 1,
    panX: 0,
    panY: 0,
    workspacePageLayoutMode: DEFAULT_WORKSPACE_PAGE_LAYOUT_MODE,
    selectedElementIds: [],
    selectedBlockIds: [],
    hoveredBlockId: null,
    selectedPageIds: [],
    selectionScope: null,
    isPageSelected: false,
    selectedTableColumn: null,
    selectedTableRow: null,
    selectedTableCells: null,
    editingTextId: null,
    editingTableCellId: null,

    editingGroupId: null,
    hasClipboard: false,
    canUndo: false,
    canRedo: false,
    textDrawMode: null,
    tableDrawMode: false,
    shapeDrawKind: null as import('@/components/canvas/elements/shape/toolbar/shapeInsertKinds').ShapeInsertKind | null,
    penToolActive: false,
    freehandToolActive: false,
    eraserToolActive: false,
    freehandStrokeColor: '#e74c3c',
    freehandStrokeWidth: 3,
    eraserSize: 24,
    activePageDomRef,
    stageHostDomRef,

    _history: [],
    _historyIndex: -1,
    _pendingBase: null,
    _copiedElements: [],
    _clipboardMode: 'copy' as const,

    ...createViewportSlice(deps),
    ...createDocumentSlice(deps),
    ...createSelectionSlice(deps),
    ...createHistoryActions(deps),
    ...createLoadStateAction(deps),
    ...createToolsSlice(deps),
    ...createBlocksSlice(deps),
  };
});
